import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import NotificationSchema, {
  NotificationJSON,
  NotificationParams,
} from "notifications/entities/notification/notification-schema";
import { Action } from "notifications/values/action/action";
import NotificationMessage from "notifications/values/notification-message/notification-message";
import NotificationRecipient from "notifications/values/notification-recipient/notification-recipient";
import NotificationTopic from "notifications/values/notification-topic/notification-topic";
import RecipientName from "notifications/values/recipient-name/recipient-name";
import SenderName from "notifications/values/sender-name/sender-name";

export default class Notification {
  public static create(values: NotificationParams): Notification {
    const parsed = NotificationSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid notification parameters.");
    }
    return new Notification(parsed);
  }

  public static fromJSON(value: NotificationJSON): Notification {
    const parsed = NotificationSchema.deserialize(value);
    return new Notification(parsed);
  }

  public static seenByUserAt(notification?: Notification, userId?: Guid): AHDate | null {
    if (!(notification instanceof Notification)) return null;
    if (!userId) {
      console.warn("User ID is undefined. Cannot get seen date.");
      return null;
    }
    if (notification.senderUserId?.isEqualTo(userId)) return notification.createdDate;
    const recipient = notification.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.seenAt : null;
  }

  public static isSeenByUser(notification?: Notification, userId?: Guid): boolean {
    if (!(notification instanceof Notification)) return false;
    if (!userId) {
      console.warn("User ID is undefined. Cannot check if notification is seen.");
      return false;
    }
    if (notification.senderUserId?.isEqualTo(userId)) return true;
    const recipient = notification.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.isSeen : false;
  }

  public static userIsRecipient(notification?: Notification, userId?: Guid): boolean {
    if (!(notification instanceof Notification)) return false;
    if (!userId) {
      console.warn("User ID is undefined. Cannot check if notification has recipient.");
      return false;
    }
    return notification.recipients.some((recipient) => recipient.userId.isEqualTo(userId));
  }

  public readonly id: Guid;
  public readonly recipients: NotificationRecipient[] = [];
  public readonly senderUserId?: Guid;
  public readonly message?: NotificationMessage;
  public readonly topic: NotificationTopic;
  public readonly actionRequired?: Action;
  public readonly createdDate: AHDate;
  public readonly isCanceled?: boolean;
  public readonly senderName?: SenderName;
  public readonly recipientName?: RecipientName;

  public constructor(values: NotificationParams) {
    const validatedValues = NotificationSchema.validate(values);
    this.id = validatedValues.id;
    this.recipients = validatedValues.recipients;
    this.senderUserId = validatedValues.senderUserId;
    this.message = validatedValues.message;
    this.topic = validatedValues.topic;
    this.actionRequired = validatedValues.actionRequired;
    this.createdDate = validatedValues.createdDate ?? new AHDate();
    this.isCanceled = validatedValues.isCanceled;
    this.senderName = validatedValues.senderName;
    this.recipientName = validatedValues.recipientName;
  }

  public toJSON(): NotificationJSON | null | undefined {
    return NotificationSchema.serialize(this);
  }

  public seenByUserAt(userId?: Guid): AHDate | null {
    return Notification.seenByUserAt(this, userId);
  }

  public isSeenByUser(userId?: Guid): boolean {
    return Notification.isSeenByUser(this, userId);
  }

  public markSeenForUser(userId?: Guid): void {
    if (!userId) {
      console.warn("User ID is undefined. Cannot mark notification as seen.");
      return;
    }
    const recipientIndex = this.recipients.findIndex((recipient) => recipient.userId.isEqualTo(userId));
    if (recipientIndex >= 0) {
      const updatedRecipient = NotificationRecipient.create({
        userId: this.recipients[recipientIndex].userId,
        seenAt: new AHDate(),
      });
      this.recipients[recipientIndex] = updatedRecipient;
    } else {
      console.warn(`User with ID ${userId.value} is not a recipient of this notification.`);
    }
  }

  public userIsRecipient(userId: Guid | undefined) {
    return Notification.userIsRecipient(this, userId);
  }

  public isEqualTo(other: Notification | null | undefined): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toString(): string {
    return `Notification(${this.id.value})`;
  }
}
